import { clamp } from '@theme/utilities';

const MIN_POSITION = 0;
const MAX_POSITION = 100;

/**
 * Before/after image comparison slider.
 * Starts at 0% (after image fully visible); drag right to reveal the before image.
 */
class BeforeAfter extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {DOMRect | null} */
  #rect = null;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLInputElement | null} */
  #input = null;

  /** @type {HTMLElement | null} */
  #handle = null;

  connectedCallback() {
    this.#viewport = this.querySelector('.before-after__viewport');
    this.#input = this.querySelector('.before-after__input');
    this.#handle = this.querySelector('.before-after__handle');

    if (!this.#viewport || !this.#input) return;

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const passive = { passive: true, signal };

    this.#viewport.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.#viewport.addEventListener('pointermove', this.#onPointerMove, passive);
    this.#viewport.addEventListener('pointerup', this.#onPointerUp, passive);
    this.#viewport.addEventListener('pointercancel', this.#onPointerUp, passive);

    this.#input.addEventListener('input', this.#onInputChange, { signal });
    this.#input.addEventListener('change', this.#onInputChange, { signal });

    this.#resizeObserver.observe(this.#viewport);

    this.#setPosition(Number(this.#input.value) || MIN_POSITION, false);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#controller?.abort();
    this.#controller = null;
    this.#cancelAnimationFrame();
    this.#rect = null;
    this.#isDragging = false;
    this.removeAttribute('dragging');
  }

  #resizeObserver = new ResizeObserver(() => {
    this.#rect = null;
  });

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (event.button !== 0 || !this.#viewport) return;

    this.#isDragging = true;
    this.#rect = this.#viewport.getBoundingClientRect();
    this.setAttribute('dragging', '');
    this.#viewport.setPointerCapture(event.pointerId);
    this.#updatePositionFromPointer(event.clientX);
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (!this.#isDragging) return;
    this.#schedulePositionUpdate(event.clientX);
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (!this.#isDragging || !this.#viewport) return;

    this.#isDragging = false;
    this.#rect = null;
    this.removeAttribute('dragging');
    this.#cancelAnimationFrame();

    if (this.#viewport.hasPointerCapture(event.pointerId)) {
      this.#viewport.releasePointerCapture(event.pointerId);
    }
  };

  /**
   * @param {Event} event
   */
  #onInputChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    this.#setPosition(Number(target.value), true);
  };

  /**
   * @param {number} clientX
   */
  #schedulePositionUpdate(clientX) {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updatePositionFromPointer(clientX);
    });
  }

  /**
   * @param {number} clientX
   */
  #updatePositionFromPointer(clientX) {
    if (!this.#rect || this.#rect.width <= 0) return;

    const percent = ((clientX - this.#rect.left) / this.#rect.width) * MAX_POSITION;
    this.#setPosition(percent, true);
  }

  #cancelAnimationFrame() {
    if (this.#rafId === null) return;
    cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
  }

  /**
   * @param {number} percent
   * @param {boolean} syncInput
   */
  #setPosition(percent, syncInput) {
    const position = clamp(Math.round(percent), MIN_POSITION, MAX_POSITION);

    this.style.setProperty('--before-after-position', `${position}%`);

    if (syncInput && this.#input) {
      this.#input.value = String(position);
      this.#input.setAttribute('aria-valuenow', String(position));
    }
  }
}

if (!customElements.get('before-after')) {
  customElements.define('before-after', BeforeAfter);
}
